import { Stack, useMediaQuery, useTheme } from '@mui/material';
import { AWTriggers } from 'enums';
import { GrammarReplacement, HighlightTexts, HighlightType, Paraphrase } from 'features/augmented-writing/augmented-writing.types';
import React, { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import ReactQuill from 'react-quill';
import { makeStyles } from 'tss-react/mui';
import { allowTextGen } from 'utils/blacklist.utils';
import CustomQuillEditor from '../../augmented-writing/editor/custom-quill-editor';
import PaiButton from '../../ui/base-components/pai-button';
import PaiLink from '../../ui/base-components/pai-link';

interface TextGenerationEditorProps {
  highlights: HighlightType[];
  grammarReplacements: GrammarReplacement[];
  paraphrases: Paraphrase[];
  optimizationMatch: number | null;
  dynamicStyles: string;
  highlightTexts: HighlightTexts | undefined;
  modifiedHighlightTexts: HighlightTexts | undefined;
  textAnalysisLoading: boolean;

  clickedChartCategory: string | string[] | undefined;
  clickedSidebarNotesCategory: string | string[] | undefined;

  hideProfileSelection: boolean;
  hideTextGeneration: boolean;
  textGenerationLoading: boolean;

  toggleHideTextGeneration: () => void;

  selectActiveSuggestion: (event: React.MouseEvent<HTMLElement>) => void;

  checkIfEmpty: (text: string) => void;

  insertAtPosition: (word: string, pos: number | number[]) => () => void;

  skipSuggestion: (content: any, id: string) => () => void;

  ignoreSuggestion: (content: any, id: string) => () => void;

  analyzeText: (trigger: AWTriggers) => () => void;

  analyzeTextQuick: () => void;

  isAnalysisPossible: () => boolean;

  handleEditorFocus(): void;
}

const TextGenerationEditor = forwardRef<ReactQuill, TextGenerationEditorProps>(
  function TextGenerationEditor(
    {
      hideTextGeneration,
      toggleHideTextGeneration,
      highlights,
      grammarReplacements,
      paraphrases,
      optimizationMatch,
      textGenerationLoading,
      textAnalysisLoading,
      clickedChartCategory,
      clickedSidebarNotesCategory,
      hideProfileSelection,
      dynamicStyles,
      modifiedHighlightTexts,
      highlightTexts,
      selectActiveSuggestion,
      checkIfEmpty,
      insertAtPosition,
      skipSuggestion,
      ignoreSuggestion,
      analyzeText,
      analyzeTextQuick,
      isAnalysisPossible,
      handleEditorFocus,
    },
    ref
  ) {
    const { classes } = useStyles();
    const { t } = useTranslation();
    const theme = useTheme();
    const downLG = useMediaQuery(theme.breakpoints.down('lg'));

    const isTextGenAllowed = allowTextGen();

    return (
      <Stack className={classes.column}>
        {isTextGenAllowed && hideTextGeneration ? (
          <div className={classes.textGenToggleContainer}>
            <PaiLink text={t('useGeneratedText')} size={'md'} underline={'always'} action={toggleHideTextGeneration} />
          </div>
        ) : null}
        <div className={classes.editorContainer}>
          <CustomQuillEditor
            ref={ref}
            placeholder={isTextGenAllowed ? t('textGenerationEditorPlaceholder') : t('addText')}
            highlights={highlights}
            grammarReplacements={grammarReplacements}
            paraphrases={paraphrases}
            optimizationMatch={optimizationMatch}
            loading={textGenerationLoading || textAnalysisLoading}
            height={downLG ? 400 : '100%'}
            disableWordCount={!hideProfileSelection}
            dynamicStyles={dynamicStyles}
            highlightTexts={modifiedHighlightTexts ? modifiedHighlightTexts : highlightTexts}
            clickedChartCategory={clickedChartCategory}
            clickedSidebarNotesCategory={clickedSidebarNotesCategory}
            selectActiveSuggestion={selectActiveSuggestion}
            checkIfEmpty={checkIfEmpty}
            insertAtPosition={insertAtPosition}
            skipSuggestion={skipSuggestion}
            ignoreSuggestion={ignoreSuggestion}
            handleEditorFocus={handleEditorFocus}
          />
        </div>
        <Stack className={classes.editorActions} direction={'row'} gap={1}>
          <PaiButton
            text={t('analyzeTextButtonTextShort')}
            size={'normal'}
            fullWidth={true}
            action={analyzeText(AWTriggers.Manual)}
            disabled={!isAnalysisPossible()}
          />
          <PaiButton text={t('autoOptimize')} size={'normal'} fullWidth={true} action={analyzeTextQuick} disabled={!isAnalysisPossible()} />
        </Stack>
      </Stack>
    );
  }
);

const useStyles = makeStyles()((theme) => ({
  column: {
    position: 'relative',
    height: '100%',
    border: `2px solid ${theme.palette.backgrounds.main}`,
    width: '100%',
    [theme.breakpoints.down('xl')]: {
      height: '640px',
      width: '100%',
    },
    [theme.breakpoints.down('lg')]: {
      height: 'unset',
      width: '100%',
    },
  },
  textGenToggleContainer: {
    position: 'absolute',
    top: '-28px',
    left: 0,
  },
  editorContainer: {
    height: 'calc(100% - 48px)',
  },
  editorActions: {
    padding: '8px 16px',
  },
}));

export default TextGenerationEditor;
